import { FC, useState } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
// Components
import {
  FormLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  IconButton,
  TextField,
  InputAdornment
} from '@mui/material';
import { ExpandMore, Delete } from '@mui/icons-material';
// Types
import { IOrderDetailPart, IUndefinedPart } from '../../../models';
import { UNDEFINED_PART, formatMoney } from '../../../helpers';
import { VendorRow } from './vendor-row';
import { PriceRow } from './price-row';
import { PartNameRow } from './part-name-row';

interface IMobileParts {
  original: IOrderDetailPart;
  handleDelete?: (rowIndex: number) => void;
  handleVendorChange: (rowIndex: number, val: number, price: number) => void;
  handleQtyChange: (rowIndex: number, val: string) => void;
  handlePriceChange: (rowIndex: number, val: number | string | undefined) => void;
  handlePartNameChange: (rowIndex: number, val: number | string | undefined) => void;
  handleUndefinedPartsCallback: (val: any) => void;
  isModal: boolean;
  index: number;
  isLoadingUndefinedParts: boolean;
  isReturnPOOrderType: boolean;
  showPrice: boolean;
  undefinedParts: IUndefinedPart[];
  isEdit?: boolean; // For different logic on order management screen
}

export const MobileParts: FC<IMobileParts> = ({
  original,
  handleDelete,
  handlePartNameChange,
  handleVendorChange,
  handleQtyChange,
  handleUndefinedPartsCallback,
  isModal,
  index,
  handlePriceChange,
  isLoadingUndefinedParts,
  isReturnPOOrderType,
  showPrice,
  undefinedParts,
  isEdit = false,
  ...props
}) => {
  const classes = useStyles();
  const {
    partName,
    quantity,
    pricePerUnit,
    price,
    undefinedPartDescriptionId,
    unit,
    hasPartPermission
  } = original;
  const [qty, setQty] = useState(quantity ? `${quantity}` : '');

  const renderPriceRow = () => {
    if (partName === UNDEFINED_PART ?? !!undefinedPartDescriptionId) {
      return (
        <PriceRow
          handlePriceChange={(pricePerUnit: number | undefined) => {
            handlePriceChange(index, pricePerUnit);
          }}
          isLoading={isLoadingUndefinedParts}
          value={pricePerUnit || price}
          isDisabled={isReturnPOOrderType}
        />
      );
    } else if (pricePerUnit || price) {
      return (
        <Typography component="span" variant="body2">
          {formatMoney(pricePerUnit || price, 2)}
        </Typography>
      );
    } else {
      return (
        <Typography component="span" variant="body2">
          --
        </Typography>
      );
    }
  };
  return (
    <Accordion className={classes.root}>
      <AccordionSummary
        className={classes.accordion}
        expandIcon={<ExpandMore />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <div className={classes.topPanelSummaryWrapper}>
          <Typography variant="body2">
            <span
              className={
                partName === UNDEFINED_PART || !!undefinedPartDescriptionId
                  ? classes.undefinedPart
                  : undefined
              }
            >
              {!!undefinedPartDescriptionId ? UNDEFINED_PART : partName}
            </span>
          </Typography>
          <div className={classes.buttonsWrapper}>
            {handleDelete && !isModal && (
              <IconButton
                className={classes.deleteButton}
                color="default"
                onClick={e => {
                  e.stopPropagation();
                  handleDelete(index);
                }}
                size="large"
              >
                <Delete />
              </IconButton>
            )}
          </div>
        </div>
      </AccordionSummary>
      <AccordionDetails className={classes.details}>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Part:
          </FormLabel>
          {partName === UNDEFINED_PART || !!undefinedPartDescriptionId ? (
            <PartNameRow
              id={original.id ?? original.partId}
              part={original}
              handlePartNameChange={(undefinedPartDescriptionId: number) => {
                handlePartNameChange(index, undefinedPartDescriptionId);
              }}
              isLoading={isLoadingUndefinedParts}
              undefinedParts={undefinedParts}
              updateOptions={callback => {
                handleUndefinedPartsCallback(callback);
              }}
            />
          ) : (
            <Typography
              component="span"
              variant="body2"
              className={
                partName === UNDEFINED_PART || !!undefinedPartDescriptionId
                  ? classes.undefinedPart
                  : undefined
              }
            >
              {partName || '--'}
            </Typography>
          )}
        </div>
        <div className={classes.panelSummaryWrapperFlex}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Qty:
          </FormLabel>
          {(isEdit &&
            hasPartPermission &&
            partName !== UNDEFINED_PART &&
            !undefinedPartDescriptionId) ||
          !isEdit ? (
            <TextField
              disabled={isModal}
              fullWidth
              variant="outlined"
              autoComplete="nope"
              name="quantity"
              size="small"
              value={qty}
              InputProps={{
                endAdornment: unit ? <InputAdornment position="end">{unit}</InputAdornment> : null
              }}
              // check for both of these since we have two different models for order detail part and place order part
              onBlur={() => handleQtyChange(index, qty)}
              onChange={e => {
                const re = /^[0-9\b]+$/;
                // make sure it is number only before we update state
                if (e.target.value === '' || re.test(e.target.value)) {
                  setQty(e.target.value);
                }
              }}
            />
          ) : (
            <Typography component="span" variant="body2">
              {qty}
            </Typography>
          )}
        </div>
        <div className={classes.panelSummaryWrapperFlex}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Vendor:
          </FormLabel>
          {partName !== UNDEFINED_PART && !undefinedPartDescriptionId ? (
            <VendorRow
              isDisabled={isModal}
              part={original}
              handleVendorChange={(vendorId: number, pricePerUnit: number) => {
                // check for both of these since we have two different models for order detail part and place order part
                handleVendorChange(index, vendorId, pricePerUnit);
              }}
            />
          ) : (
            <Typography component="span" variant="body2">
              --
            </Typography>
          )}
        </div>
        {showPrice && (
          <div className={classes.panelSummaryWrapper}>
            <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
              Price:
            </FormLabel>

            {renderPriceRow()}
          </div>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    overflowX: 'hidden'
  },
  accordion: {
    padding: '0 16px',
    '&& .MuiAccordionSummary-expandIcon': {
      padding: 3
    }
  },
  topPanelSummaryWrapper: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    '&:first-child': { marginTop: 0 }
  },
  panelSummaryWrapper: {
    margin: theme.spacing(0.5, 0)
  },
  panelSummaryWrapperFlex: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(0.5, 0),
    '&:first-child': { marginTop: 0 }
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 16px',
    backgroundColor: theme.palette.grey[100]
  },
  roleWrapper: {
    display: 'flex',
    alignItems: 'center'
  },
  editButton: {
    padding: `2px 5px`
  },
  deleteButton: {
    padding: `2px 5px`,
    color: theme.palette.error.main
  },
  boldName: {
    color: theme.palette.typography.labels,
    fontWeight: 600
  },
  subLabel: {
    marginRight: 10,
    fontSize: theme.typography.body2.fontSize
  },
  buttonsWrapper: {
    display: 'flex'
  },
  undefinedPart: {
    color: theme.palette.error.main
  }
}));
